import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '36px' }}>
      <h2 style={{
        fontFamily: "'Syne', sans-serif",
        fontSize: '20px', fontWeight: 700, color: '#0f172a',
        letterSpacing: '-0.01em', marginBottom: '12px',
        paddingBottom: '10px', borderBottom: '1px solid #f1f5f9',
      }}>{title}</h2>
      <div style={{ fontSize: '15px', color: '#475569', lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8fafc; -webkit-text-size-adjust: 100%; }
        a { color: #3b82f6; text-decoration: none; }
        a:hover { text-decoration: underline; }
      `}</style>

      {/* Top navbar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #f1f5f9',
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📦</div>
          <span style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>StockSpace</span>
        </Link>
        <Link to="/" style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>← Back to home</Link>
      </div>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0c1a3a 0%, #1e3a8a 60%, #1d4ed8 100%)',
        padding: '56px 24px 48px',
        textAlign: 'center',
      }}>
        <div style={{ display: 'inline-block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#60a5fa', background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px', padding: '4px 12px', marginBottom: '16px' }}>Legal</div>
        <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '36px', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: '10px' }}>Privacy Policy</h1>
        <p style={{ fontSize: '15px', color: '#93c5fd' }}>Last updated: June 2025 &nbsp;·&nbsp; Effective: June 2025</p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>

        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px 20px', marginBottom: '36px', fontSize: '14px', color: '#1e40af', lineHeight: 1.6 }}>
          <strong>Summary:</strong> StockSpace collects only what it needs to run your inventory workspace. We don't sell your data, show you ads, or share your information with third parties except Firebase (our infrastructure provider). This policy explains exactly what we collect and why.
        </div>

        <Section title="1. Who We Are">
          <p>StockSpace is a free inventory management application operated by <strong>StockSpace</strong> ("we", "us", "our"). For any privacy-related questions, contact us at <a href="mailto:stockspacesupport@gmail.com">stockspacesupport@gmail.com</a>.</p>
        </Section>

        <Section title="2. What Information We Collect">
          <p style={{ marginBottom: '12px' }}>We collect the following information when you use StockSpace:</p>
          <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Account information:</strong> Your name, email address, and password (stored securely via Firebase Authentication). If you sign in with Google or GitHub, we receive your name and email from those providers.</li>
            <li><strong>Organisation data:</strong> Your business/organisation name that you provide during signup.</li>
            <li><strong>Inventory data:</strong> Product names, stock quantities, groups, and transaction logs that you enter into the app.</li>
            <li><strong>Usage data:</strong> Basic technical data such as your browser type and IP address, collected automatically by Firebase for security and performance purposes.</li>
          </ul>
          <p style={{ marginTop: '12px' }}>We do <strong>not</strong> collect payment information (StockSpace is free), location data beyond IP address, or any sensitive personal information.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li>To create and manage your account and organisation workspace</li>
            <li>To store and sync your inventory data across devices in real time</li>
            <li>To send password reset emails when you request them</li>
            <li>To allow team members you invite to access your shared workspace</li>
            <li>To improve and maintain the application</li>
          </ul>
          <p style={{ marginTop: '12px' }}>We do <strong>not</strong> use your data for advertising, profiling, or sell it to any third party.</p>
        </Section>

        <Section title="4. Data Storage and Security">
          <p style={{ marginBottom: '12px' }}>Your data is stored on <strong>Google Firebase</strong> (Firestore and Firebase Authentication), a secure cloud platform provided by Google LLC. Firebase complies with ISO 27001, SOC 1, SOC 2, and SOC 3 standards.</p>
          <p style={{ marginBottom: '12px' }}>All data is transmitted over HTTPS (TLS encryption). Access to your organisation's data is controlled by role-based security rules — only authenticated members of your organisation can read or write your data.</p>
          <p>While we take reasonable precautions, no internet transmission is 100% secure. Use a strong password and do not share your credentials.</p>
        </Section>

        <Section title="5. Third-Party Services">
          <p style={{ marginBottom: '12px' }}>StockSpace uses the following third-party services:</p>
          <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Google Firebase</strong> — authentication, database, and hosting. <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">Firebase Privacy Policy</a></li>
            <li><strong>Google OAuth</strong> (optional) — if you choose to sign in with Google. <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy</a></li>
            <li><strong>GitHub OAuth</strong> (optional) — if you choose to sign in with GitHub. <a href="https://docs.github.com/en/site-policy/privacy-policies/github-general-privacy-statement" target="_blank" rel="noreferrer">GitHub Privacy Policy</a></li>
          </ul>
          <p style={{ marginTop: '12px' }}>We do not use any advertising networks, analytics trackers (e.g. Google Analytics), or marketing platforms.</p>
        </Section>

        <Section title="6. Cookies and Local Storage">
          <p>StockSpace does not use tracking cookies or advertising cookies. Firebase Authentication stores a session token in your browser's <strong>localStorage</strong> to keep you signed in between visits. This is strictly necessary for the app to function and does not track you across websites. You can clear this at any time by signing out or clearing your browser data.</p>
        </Section>

        <Section title="7. Data Retention">
          <p style={{ marginBottom: '12px' }}>Your data is retained as long as your account is active. If you wish to delete your account and all associated data, contact us at <a href="mailto:stockspacesupport@gmail.com">stockspacesupport@gmail.com</a> and we will delete your data within 30 days.</p>
          <p>Organisation admins can delete products and activity logs directly within the app at any time.</p>
        </Section>

        <Section title="8. Your Rights">
          <p style={{ marginBottom: '12px' }}>Under applicable Indian law (IT Act, 2000 and the Digital Personal Data Protection Act, 2023) and general best practice, you have the right to:</p>
          <ul style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <li><strong>Access</strong> the personal data we hold about you</li>
            <li><strong>Correct</strong> inaccurate data (you can update your name and email in Settings)</li>
            <li><strong>Delete</strong> your account and data by contacting us</li>
            <li><strong>Object</strong> to processing of your data</li>
          </ul>
          <p style={{ marginTop: '12px' }}>To exercise any of these rights, email <a href="mailto:stockspacesupport@gmail.com">stockspacesupport@gmail.com</a>.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>StockSpace is intended for business use and is not directed at children under the age of 13. We do not knowingly collect data from children. If you believe a child has provided us with personal information, contact us immediately.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Continued use of StockSpace after changes constitutes acceptance of the updated policy. For significant changes, we will notify you via email.</p>
        </Section>

        <Section title="11. Contact Us">
          <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us:</p>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', marginTop: '12px' }}>
            <p><strong>StockSpace</strong></p>
            <p>Email: <a href="mailto:stockspacesupport@gmail.com">stockspacesupport@gmail.com</a></p>
            <p>GitHub: <a href="https://github.com/KavyaSingla244" target="_blank" rel="noreferrer">github.com/KavyaSingla244</a></p>
          </div>
        </Section>

        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Link to="/" style={{ fontSize: '14px', color: '#3b82f6', fontWeight: 500 }}>← Back to StockSpace</Link>
          <Link to="/tos" style={{ fontSize: '14px', color: '#3b82f6', fontWeight: 500 }}>Terms of Service →</Link>
        </div>
      </div>
    </>
  );
}